"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AbstractLogger = exports.AbstractLoggerScope = void 0;
const tslib_1 = require("tslib");
const _1 = require(".");
/**
 * Default scope key for the Abstract Logger
 */
exports.AbstractLoggerScope = '@sensenet/client-utils/AbstractLogger';
/**
 * Abstract logger instance
 */
class AbstractLogger {
    constructor() {
        /**
         * Returns an object that contains shortcuts to the original logger that contains the provided scope.
         * usage example:
         * ```ts
         *  const scopedLogger = myLogger.withScope("myLogScope")
         *  scopedLogger.information({message: "foo"}) // will add an information entry with the provided scope
         * ```
         */
        this.withScope = (scope) => ({
            /**
             * Adds a custom log entry
             */
            addEntry: (entry) => this.addEntry(Object.assign({ scope }, entry)),
            /**
             * Adds a Verbose log entry. Verbose is the noisiest level, rarely (if ever) enabled for a production app.
             */
            verbose: (entry) => this.verbose(Object.assign({ scope }, entry)),
            /**
             * Adds a debug log entry. Debug is used for internal system events that are not necessarily observable from the outside, but useful when determining how something happened.
             */
            debug: (entry) => this.debug(Object.assign({ scope }, entry)),
            /**
             * Adds an Information log entry. Information events describe things happening in the system that correspond to its responsibilities and functions. Generally these are the observable actions the system can perform.
             */
            information: (entry) => this.information(Object.assign({ scope }, entry)),
            /**
             * Adds a Warning log entry. When service is degraded, endangered, or may be behaving outside of its expected parameters, Warning level events are used.
             */
            warning: (entry) => this.warning(Object.assign({ scope }, entry)),
            /**
             * Adds an Error log entry. When functionality is unavailable or expectations broken, an Error event is used.
             */
            error: (entry) => this.error(Object.assign({ scope }, entry)),
            /**
             * Adds a Fatal log entry. The most critical level, Fatal events demand immediate attention.
             */
            fatal: (entry) => this.fatal(Object.assign({ scope }, entry)),
        });
    }
    addEntryInternal(entry, isVerbose) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            try {
                yield this.addEntry(entry, isVerbose);
            }
            catch (error) {
                this.error({
                    scope: exports.AbstractLoggerScope,
                    message: 'There was an error adding entry to the log',
                    data: {
                        entry,
                        error,
                    },
                });
            }
        });
    }
    /**
     * Adds a Verbose level log entry. Verbose is the noisiest level, rarely (if ever) enabled for a production app.
     * @param entry The Log entry
     */
    verbose(entry) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            yield this.addEntryInternal(Object.assign(Object.assign({}, entry), { level: _1.LogLevel.Verbose }), true);
        });
    }
    /**
     * Adds a Debug level log entry. Debug is used for internal system events that are not necessarily observable from the outside, but useful when determining how something happened.
     * @param entry  The Log entry
     */
    debug(entry) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            yield this.addEntryInternal(Object.assign(Object.assign({}, entry), { level: _1.LogLevel.Debug }));
        });
    }
    /**
     * Adds an Information level log entry. Information events describe things happening in the system that correspond to its responsibilities and functions. Generally these are the observable actions the system can perform.
     * @param entry  The Log entry
     */
    information(entry) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            yield this.addEntryInternal(Object.assign(Object.assign({}, entry), { level: _1.LogLevel.Information }));
        });
    }
    /**
     * Adds a Warning level log entry. When service is degraded, endangered, or may be behaving outside of its expected parameters, Warning level events are used.
     * @param entry  The Log entry
     */
    warning(entry) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            yield this.addEntryInternal(Object.assign(Object.assign({}, entry), { level: _1.LogLevel.Warning }));
        });
    }
    /**
     * Adds an Error level log entry. When functionality is unavailable or expectations broken, an Error event is used.
     * @param entry  The Log entry
     */
    error(entry) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            try {
                yield this.addEntry(Object.assign(Object.assign({}, entry), { level: _1.LogLevel.Error }));
            }
            catch (error) {
                yield this.fatal({
                    scope: exports.AbstractLoggerScope,
                    message: 'There was an error persisting an Error event in the log and therefore the event was elevated to Fatal level.',
                    data: {
                        originalEntry: entry,
                        error,
                    },
                });
            }
        });
    }
    /**
     * Adds a Fatal level log entry. The most critical level, Fatal events demand immediate attention.
     * @param entry  The Log entry
     */
    fatal(entry) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            yield this.addEntry(Object.assign(Object.assign({}, entry), { level: _1.LogLevel.Fatal }));
        });
    }
}
exports.AbstractLogger = AbstractLogger;
//# sourceMappingURL=abstract-logger.js.map